<template>
  <div>
    <div
      v-for="section in sections"
      :key="section.key"
      class="mb-4"
    >
      <div class="d-flex align-items-center justify-content-between mb-2">
        <h5 class="font-w600 my-0">
          {{ section.title }}
          <span
            v-if="section.images.length"
            class="text-muted font-w400 font-size-sm"
          >({{ section.images.length }})</span>
        </h5>
        <div class="d-flex align-items-center">
          <b-button
            v-if="$can('upload_photo', workOrder)"
            variant="outline-secondary"
            size="sm"
            class="mr-2"
            v-b-modal="section.addModalId"
          >
            <i class="fa fa-plus mr-1" /> Add
          </b-button>
          <sfs-download-button
            v-if="section.images.length"
            variant="outline-secondary"
            size="sm"
            :filename="section.zipFilename"
            :provider-params="{ id: workOrder.id }"
            :file-provider="section.zipProvider"
            v-b-tooltip.hover
            :title="`Download ${section.title}`"
          />
        </div>
      </div>

      <div
        v-if="!section.images.length"
        class="text-muted mb-3"
      >
        {{ section.emptyText }}
      </div>

      <div
        v-else
        class="d-flex flex-wrap"
      >
        <b-col
          v-for="(image, index) in section.images"
          :key="image.id"
          cols="6"
          md="4"
          lg="3"
          xl="2"
          class="mb-3 px-2"
        >
          <div
            class="options-container rounded bg-light shadow"
            role="button"
            @click="openPhoto(section.key, index)"
          >
            <img
              class="w-100 photo-thumb"
              :src="image.url"
              :alt="section.title"
            >
            <div class="options-overlay bg-black-50">
              <div class="options-overlay-content d-flex">
                <b-button
                  variant="light"
                  size="sm"
                  class="mr-1"
                  v-b-tooltip.hover
                  title="View Photo"
                  @click.stop="openPhoto(section.key, index)"
                >
                  <i class="fa fa-eye" />
                </b-button>
                <b-button
                  variant="light"
                  size="sm"
                  class="mr-1"
                  :href="image.url"
                  download
                  v-b-tooltip.hover
                  title="Download Photo"
                  @click.stop
                >
                  <i class="fa fa-download" />
                </b-button>
                <b-button
                  v-if="$can('delete_photo', workOrder)"
                  variant="light"
                  size="sm"
                  v-b-tooltip.hover
                  title="Remove the Photo"
                  @click.stop="removePhoto(section, image)"
                >
                  <i class="fa fa-trash-alt" />
                </b-button>
              </div>
            </div>
            <div class="font-size-xs text-muted px-2 py-1 bg-white border-top">
              {{ caption(image) }}
            </div>
          </div>
        </b-col>
      </div>
    </div>

    <b-modal
      id="technician-work-order-photo-viewer-modal"
      :title="viewerTitle"
      size="lg"
      no-close-on-backdrop
      hide-footer
      static
      @hidden="activeSectionKey = null"
    >
      <template #modal-header-close>
        <i class="fa fa-close" />
      </template>

      <template v-if="activeSection && activePhoto">
        <div class="photo-viewer-stage rounded mb-3 position-relative">
          <img
            class="photo-viewer-image"
            :src="activePhoto.url"
            :alt="activeSection.title"
          >

          <div
            v-if="activeSection.images.length > 1"
            class="photo-viewer-nav d-flex align-items-center"
          >
            <b-button
              variant="link"
              class="text-white p-1"
              :disabled="photoIndex === 0"
              @click="photoIndex -= 1"
            >
              <i class="fa fa-chevron-left" />
            </b-button>
            <span class="text-white font-size-sm px-2">
              {{ photoIndex + 1 }} / {{ activeSection.images.length }}
            </span>
            <b-button
              variant="link"
              class="text-white p-1"
              :disabled="photoIndex >= activeSection.images.length - 1"
              @click="photoIndex += 1"
            >
              <i class="fa fa-chevron-right" />
            </b-button>
          </div>
        </div>

        <div class="d-flex align-items-center justify-content-between mb-3">
          <div>
            <div class="font-w600">
              {{ caption(activePhoto) }}
            </div>
            <div class="text-muted font-size-sm">
              {{ activeSection.title }}
            </div>
          </div>
          <div>
            <b-button
              variant="outline-secondary"
              size="sm"
              class="mr-1"
              :href="activePhoto.url"
              download
              v-b-tooltip.hover
              title="Download Photo"
            >
              <i class="fa fa-download" />
            </b-button>
            <b-button
              v-if="$can('delete_photo', workOrder)"
              variant="outline-danger"
              size="sm"
              v-b-tooltip.hover
              title="Remove the Photo"
              @click="removePhoto(activeSection, activePhoto)"
            >
              <i class="fa fa-trash-alt" />
            </b-button>
          </div>
        </div>
      </template>

      <b-button
        variant="primary"
        block
        @click="$bvModal.hide('technician-work-order-photo-viewer-modal')"
      >
        Close
      </b-button>
    </b-modal>

    <new-photo-modal
      v-for="section in sections"
      :key="section.addModalId"
      :title="section.addModalTitle"
      :modal-id="section.addModalId"
      :work-order-id="workOrder.id"
      :before-photo="section.beforePhoto"
      :after-photo="section.afterPhoto"
      @on-save="onSave"
    />
  </div>
</template>

<script>
import NewPhotoModal from '@/components/technician/workOrders/NewPhotoModal.vue';

export default {
  name: 'TechnicianWorkOrdersPhotosPanel',
  components: {
    NewPhotoModal,
  },
  props: {
    workOrder: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      activeSectionKey: null,
      photoIndex: 0,
    };
  },
  computed: {
    sections() {
      return [
        {
          key: 'before',
          title: 'Before Photos',
          emptyText: 'No before photos have been added yet.',
          images: this.workOrder.before_photos || [],
          beforePhoto: true,
          afterPhoto: false,
          addModalId: 'technician-work-order-new-before-photo-modal',
          addModalTitle: 'New Before Photos',
          deleteProvider: this.$technicianWorkOrdersAPI.destroyBeforePhoto,
          zipProvider: this.$technicianWorkOrdersAPI.getBeforePhotosZip,
          zipFilename: `${this.workOrder.number} Before Photos.zip`,
        },
        {
          key: 'after',
          title: 'After Photos',
          emptyText: 'No after photos have been added yet.',
          images: this.workOrder.after_photos || [],
          beforePhoto: false,
          afterPhoto: true,
          addModalId: 'technician-work-order-new-after-photo-modal',
          addModalTitle: 'New After Photos',
          deleteProvider: this.$technicianWorkOrdersAPI.destroyAfterPhoto,
          zipProvider: this.$technicianWorkOrdersAPI.getAfterPhotosZip,
          zipFilename: `${this.workOrder.number} After Photos.zip`,
        },
        {
          key: 'no_show',
          title: 'No Show Photos',
          emptyText: 'No no show photos have been added yet.',
          images: this.workOrder.no_show_photos || [],
          beforePhoto: false,
          afterPhoto: false,
          addModalId: 'technician-work-order-new-no-show-photo-modal',
          addModalTitle: 'New No Show Photos',
          deleteProvider: this.$technicianWorkOrdersAPI.destroyNoShowPhoto,
          zipProvider: this.$technicianWorkOrdersAPI.getNoShowPhotosZip,
          zipFilename: `${this.workOrder.number} No Show Photos.zip`,
        },
      ];
    },
    activeSection() {
      return this.sections.find((section) => section.key === this.activeSectionKey) || null;
    },
    activePhoto() {
      return this.activeSection?.images[this.photoIndex] || null;
    },
    viewerTitle() {
      if (!this.activeSection) {
        return 'Photos';
      }
      return `${this.activeSection.title} · ${this.photoIndex + 1} of ${this.activeSection.images.length}`;
    },
  },
  watch: {
    activeSection(section) {
      if (!this.activeSectionKey || !section) {
        return;
      }
      if (!section.images.length) {
        this.$bvModal.hide('technician-work-order-photo-viewer-modal');
        return;
      }
      if (this.photoIndex >= section.images.length) {
        this.photoIndex = section.images.length - 1;
      }
    },
  },
  methods: {
    openPhoto(sectionKey, index) {
      this.activeSectionKey = sectionKey;
      this.photoIndex = index;
      this.$bvModal.show('technician-work-order-photo-viewer-modal');
    },
    removePhoto(section, photo) {
      this.$swal({
        text: 'Once it\'s deleted, it cannot be undone!',
        preConfirm: () => section.deleteProvider(this.workOrder.id, photo.id),
      }).then((result) => {
        if (result.value) {
          this.$emit('on-destroy', photo);
        }
        result.timer;
      });
    },
    onSave() {
      this.$emit('on-save');
    },
    caption(image) {
      return this.$options.filters.dateTimeFormat(image.created_at, this.workOrder.location.time_zone);
    },
  },
};
</script>

<style scoped>
.photo-thumb {
  height: 160px;
  object-fit: cover;
}

.photo-viewer-stage {
  background: #111;
  overflow: hidden;
}

.photo-viewer-image {
  display: block;
  width: 100%;
  max-height: 65vh;
  object-fit: contain;
}

.photo-viewer-nav {
  position: absolute;
  left: 50%;
  bottom: 0.75rem;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.65);
  border-radius: 2rem;
  padding: 0.15rem 0.5rem;
}

.photo-viewer-nav .btn:disabled {
  opacity: 0.35;
}
</style>
